import { useState, useEffect, useRef } from 'react'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { hasAnyRole } from '../../lib/roles'
import SearchableSelect from '../../components/SearchableSelect'

interface Branch { code: string; name: string; is_active: boolean }
interface BranchAccess { branch_code: string; access_type: string }
import AddressesTab from '../../components/JobTabs/AddressesTab'
import JobCustomerLine from '../../components/JobTabs/JobCustomerLine'
import ShipDetailsTab from '../../components/JobTabs/ShipDetailsTab'
import { isIsoContainerNumber } from '../../components/JobTabs/ContainersTab'
import GoodsTab from '../../components/JobTabs/GoodsTab'
import ChargesTab from '../../components/JobTabs/ChargesTab'
import CustomsFinancialTab from '../../components/JobTabs/CustomsFinancialTab'
import NotesTab from '../../components/JobTabs/NotesTab'
import DocumentsTab from '../../components/JobTabs/DocumentsTab'
import ConsolTab from '../../components/JobTabs/ConsolTab'
import ConsolDraftsTab, { type DraftChild, draftRefProblems, effectiveRef, REF_FORMAT } from '../../components/JobTabs/ConsolDraftsTab'
import DuplicateJobModal from '../../components/DuplicateJobModal'
import TransportTab from '../../components/JobTabs/TransportTab'
import MilestonesTab from '../../components/JobTabs/MilestonesTab'
import SyncHistoryDialog from '../../components/SyncHistoryDialog'
import SyncPreflightModal from '../../components/SyncPreflightModal'
import { BOXTOP_SYNC_ENABLED } from '../../lib/boxtopSync'
import GroupPage from './GroupPage'
import PoaStatusBadge from '../../components/PoaStatusBadge'
import { fetchPoaStatusForEori, formatPoaStatusLine, type PoaStatus } from '../../lib/poaStatus'
import { docFlagsOf, pickParty } from '../../../supabase/functions/_shared/partyScope.ts'

type TabName = 'addresses' | 'ship' | 'goods' | 'charges' | 'customs' | 'notes' | 'documents' | 'consol' | 'transport' | 'milestones'

// Z30: relaxed from the old KI/KE+5-digit-only assumption so a job_ref can be renamed to
// anything reasonable -- AutoJobs' own auto-sequence (preview_next_job_ref/generate_job_ref)
// is unaffected and still only ever produces KI/KE+5digits. Mirrors the DB check constraint
// added in 20260903d_z30_job_ref_editable.sql.
const JOB_REF_FORMAT = /^[A-Z0-9-]{1,15}$/

const TABS: { name: TabName; label: string }[] = [
  { name: 'addresses', label: 'Addresses' },
  { name: 'ship',      label: 'Ship Details' },
  { name: 'goods',     label: 'Goods' },
  { name: 'charges',   label: 'Charges' },
  { name: 'customs',   label: 'Customs & Financial' },
  { name: 'transport', label: 'Transport' },
  { name: 'milestones', label: 'Milestones' },
  { name: 'notes',     label: 'Notes' },
  { name: 'documents', label: 'Documents' },
  { name: 'consol',    label: 'Consol' },
]

export default function JobDetail() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { userEmail, userRoles } = useAuth()
  const canEditBranch = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])

  const initialState = location.state || {}
  const isNew = !id || id === 'new'
  const copiedFromRef: string | undefined = initialState.copiedFrom
  // Z36: the number came from a pre-reservation ("Use this"). It is shown read-only, nothing may change it,
  // and it is re-checked against the database on load (a refresh or a stale tab must not silently get another number).
  const reservationId: string | undefined = isNew ? initialState.reservationId : undefined
  const [reservationCheck, setReservationCheck] = useState<'checking' | 'ok' | string>(reservationId ? 'checking' : 'ok')
  useEffect(() => {
    if (!reservationId) return
    supabase.from('job_ref_reservations').select('job_ref, status').eq('id', reservationId).maybeSingle()
      .then(({ data, error }) => {
        if (error || !data) setReservationCheck('The reservation could not be found. Go back to Jobs and use it again.')
        else if (data.status !== 'active') setReservationCheck(`${data.job_ref} is no longer reserved (status: ${data.status}). Go back to Jobs and reserve a number again.`)
        else if (data.job_ref !== initialState.jobRef) setReservationCheck('The reserved number does not match this form. Go back to Jobs and use it again.')
        else setReservationCheck('ok')
      })
  }, [reservationId])
  const [showDuplicate, setShowDuplicate] = useState(false)
  // Set when this job is a shipment of a group opened with ?full=1: no charges, invoices or customs on it (they live on the group / are not used).
  const [groupParent, setGroupParent] = useState<{ id: string; ref: string } | null>(null)
  // Set when this job is a house bill of a Consol master: the header gets a button back to the master's Consol tab (next to Back to Jobs).
  const [consolParent, setConsolParent] = useState<{ id: string; ref: string } | null>(null)

  // ?tab=<name> lets other pages (e.g. Warehouse "← Back to Job") land on a specific tab.
  const [activeTab, setActiveTab] = useState<TabName>(() => {
    const t = new URLSearchParams(location.search).get('tab')
    return (TABS.find(x => x.name === t)?.name as TabName | undefined) ?? 'addresses'
  })
  // The page stays mounted when you go from one job to another (same route), and useState above only reads ?tab= once: follow it when the URL changes,
  // otherwise a link like /jobs/<master>?tab=consol would open the master on whatever tab was showing.
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab')
    const found = TABS.find(x => x.name === t)
    if (found) setActiveTab(found.name as TabName)
  }, [id, location.search])
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncHistoryOpen, setSyncHistoryOpen] = useState(false)
  // Z58.7: every sync click opens the consolidated Sync Preflight modal FIRST (InvoiceTo +
  // Shipper/Consignee/Notify, all with a visible status) instead of the old reactive flow
  // (call sync, get blocked, show one dialog at a time) -- that flow had no way to show a party
  // that never gets blocked at all (e.g. Consignee/Notify sharing InvoiceTo's own BoxTop account).
  const [pendingPreflight, setPendingPreflight] = useState<{ force: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [allBranches, setAllBranches] = useState<Branch[]>([])
  const [ownerName, setOwnerName] = useState<string | null>(null)
  const [reminders, setReminders] = useState<{ name: string; reminder: string }[]>([])
  const [reminderDismissed, setReminderDismissed] = useState(false)
  // Z30: the loaded job_ref, so Save only bothers cascading a rename when it actually changed.
  const originalJobRefRef = useRef<string | null>(null)
  const [jobRefFormatError, setJobRefFormatError] = useState<string | null>(null)
  // Z93b: draft house bills (new Consol only). Pure client state until Save.
  const [drafts, setDrafts] = useState<DraftChild[]>([])

  const [jobData, setJobData] = useState<any>({
    job_ref: initialState.jobRef || '',
    direction: initialState.direction || '',
    transport_mode: initialState.transportMode || '',
    job_type: initialState.jobType || '',
    status: 'PRE',
    branch_code: null as string | null,
    booked_date: new Date().toISOString().split('T')[0],
    importer_id: '',
    consignor_id: '',
    notify_id: '',
    invoice_to_id: '',
    overseas_agent_id: '',
    port_agent_id: '',
    shed_id: '',
    storage_location: '',
    booking_ref: '',
    client_ref: '',
    air_details: {},
    sea_details: {},
    goods: [],
    containers: [],
    notes: [],
    branch_access: [] as BranchAccess[],
    boxtrax_synced: false,
    boxtrax_ref: null as string | null,
    boxtrax_job_id: null as string | null,
    boxtrax_synced_at: null as string | null,
    boxtrax_error: null as string | null,
    // Gap-analysis fix: AutoJob always sets import_source='AUTOJOBS'/sync_status='pending' on
    // every job it creates; manual creation left both null, so a manually-created job had no
    // origin marker and started outside the sync-status lifecycle AutoJob-created jobs get.
    import_source: isNew ? 'MANUAL' : null as string | null,
    sync_status: isNew ? 'pending' : undefined as string | undefined,
  })

  useEffect(() => {
    if (isNew && jobData.job_type !== 'CONSOL' && activeTab === 'consol') setActiveTab('addresses')
  }, [isNew, jobData.job_type, activeTab])

  useEffect(() => {
    if (!isNew) loadJob(id!)
    supabase.from('branches').select('*').eq('is_active', true).order('code')
      .then(({ data }) => setAllBranches(data || []))
  }, [id])

  // #290: Owner = jobs.created_by -> user_roles.boxtop_owner_name (plain text field, no
  // lookup table). Read-only here — fix the mapping in Admin > Manage Users, not on this page.
  useEffect(() => {
    setOwnerName(null)
    if (!jobData.created_by) return
    supabase.from('user_roles').select('boxtop_owner_name').eq('user_email', jobData.created_by).maybeSingle()
      .then(({ data: userRole }) => setOwnerName(userRole?.boxtop_owner_name || null))
  }, [jobData.created_by])

  // Z73.6.1: subtle POA indicator up top, before staff even open eCustoms Preview -- import
  // jobs only, POA is only ever checked against the importer's EORI (see submit-ecustoms).
  // The importer the DECLARATION uses (HBL set on a consol child / one-master-one-house job, else the plain one): the light must check
  // that company's EORI, not the master's. Same rule as submit-ecustoms (_shared/partyScope.ts) -- if they differed, the light could be
  // green for one company while the declaration is filed for another.
  const declaredImporterId = pickParty(jobData, 'importer', docFlagsOf(jobData.sea_details, jobData.air_details)).id
  const [importerPoaStatus, setImporterPoaStatus] = useState<PoaStatus | null>(null)
  useEffect(() => {
    let cancelled = false
    setImporterPoaStatus(null)
    if (jobData.direction !== 'IMP' || !declaredImporterId) return
    ;(async () => {
      const { data: customer } = await supabase.from('customers').select('eori').eq('id', declaredImporterId).maybeSingle()
      if (cancelled || !customer?.eori) return
      const status = await fetchPoaStatusForEori(customer.eori)
      if (!cancelled) setImporterPoaStatus(status)
    })()
    return () => { cancelled = true }
  }, [jobData.direction, declaredImporterId])

  const loadJob = async (jobId: string) => {
    try {
      setLoading(true)
      setError(null)

      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .single()
      if (jobError) throw jobError

      // A shipment row of a group has no page of its own: open the group's Dashboard on that row. (?full=1 is the escape hatch, used for a row
      // with several goods lines that must be edited on the full job page.)
      let shipmentOf: { id: string; ref: string } | null = null
      let houseOf: { id: string; ref: string } | null = null
      if (job.parent_job_id) {
        const { data: parent } = await supabase.from('jobs').select('id, job_ref, job_type').eq('id', job.parent_job_id).maybeSingle()
        if (parent?.job_type === 'GROUP') {
          if (!new URLSearchParams(location.search).get('full')) { navigate(`/jobs/${parent.id}?row=${job.id}`, { replace: true }); return }
          shipmentOf = { id: parent.id, ref: parent.job_ref }
        } else if (parent?.job_type === 'CONSOL') {
          houseOf = { id: parent.id, ref: parent.job_ref }   // same query as above: the parent was already being read, the result used to be thrown away
        }
      }
      setGroupParent(shipmentOf)
      setConsolParent(houseOf)

      const [airRes, seaRes, goodsRes, containersRes, notesRes, branchAccessRes] = await Promise.all([
        supabase.from('job_air_details').select('*').eq('job_id', jobId).maybeSingle(),
        supabase.from('job_sea_details').select('*').eq('job_id', jobId).maybeSingle(),
        supabase.from('job_goods').select('*').eq('job_id', jobId).order('item_no'),
        supabase.from('job_containers').select('*').eq('job_id', jobId),
        supabase.from('job_notes').select('*').eq('job_id', jobId).order('created_at'),
        supabase.from('job_branch_access').select('branch_code,access_type').eq('job_id', jobId),
      ])

      setJobData({
        ...job,
        air_details: airRes.data || {},
        sea_details: seaRes.data || {},
        goods: goodsRes.data || [],
        containers: containersRes.data || [],
        notes: notesRes.data || [],
        branch_access: branchAccessRes.data || [],
      })
      originalJobRefRef.current = job.job_ref

      // Load reminders for the importer + consignor this job actually declares (HBL set or plain set: one shared rule, _shared/partyScope.ts)
      const reminderFlags = docFlagsOf(seaRes.data, airRes.data)
      const partyIds = [pickParty(job as any, 'importer', reminderFlags).id, pickParty(job as any, 'consignor', reminderFlags).id].filter(Boolean) as string[]
      if (partyIds.length > 0) {
        const { data: parties } = await supabase
          .from('customers')
          .select('name, reminder')
          .in('id', partyIds)
          .not('reminder', 'is', null)
          .neq('reminder', '')
        if (parties && parties.length > 0) {
          setReminders(parties as { name: string; reminder: string }[])
          setReminderDismissed(false)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job')
    } finally {
      setLoading(false)
    }
  }

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg)
    setTimeout(() => setSuccessMsg(null), 3000)
  }

  // #16: sync-to-boxtrax is the single writer of boxtrax_synced/boxtrax_ref/boxtrax_synced_at/
  // boxtrax_error/sync_status -- this used to do its own redundant client-side .update() after
  // calling the edge function (and didn't even write sync_status), a second write path that
  // could drift from what the edge function had just written. Re-fetch the job row instead of
  // duplicating those writes here.
  // Z58.7: opens the Sync Preflight modal -- the actual sync call only happens after staff
  // approves it (runBoxtraxSync below).
  const handleBoxtraxSync = (force = false) => {
    if (!jobData.id || !jobData.job_ref) return

    // KI26034 P0: forcing when BoxTop already has a JobId for this job does not update
    // that record (no update endpoint exists) -- it creates a brand-new duplicate one.
    if (force && !window.confirm(
      `BoxTop already has a record for this job${jobData.boxtrax_job_id ? ` (JobId=${jobData.boxtrax_job_id})` : ''}. ` +
      `There is no update endpoint yet, so forcing a re-sync will create a NEW, separate record in BoxTop rather than updating the existing one. Continue?`
    )) return

    setPendingPreflight({ force })
  }

  const runBoxtraxSync = async (force: boolean) => {
    if (!jobData.id || !jobData.job_ref) return

    setSyncing(true)
    setError(null)

    try {
      const syncData = {
        job_ref: jobData.job_ref,
        force,
        invoiceToConfirmed: true,
        waybill_number: jobData.sea_details?.house_bl_no || jobData.air_details?.hawb_no,
        mbl_number: jobData.sea_details?.master_bl_no || jobData.air_details?.mawb_no,
        transport_mode: jobData.transport_mode,
        direction: jobData.direction,
        shipper: jobData.sea_details?.ai_parsed_data?.shipper || jobData.air_details?.ai_parsed_data?.shipper,
        consignee: jobData.sea_details?.ai_parsed_data?.consignee || jobData.air_details?.ai_parsed_data?.consignee,
        ports: jobData.sea_details?.ai_parsed_data?.ports || jobData.air_details?.ai_parsed_data?.ports,
        cargo: jobData.sea_details?.ai_parsed_data?.cargo || jobData.air_details?.ai_parsed_data?.cargo
      }

      console.log('[JobDetail] sync-to-boxtrax request:', { job_ref: syncData.job_ref, force })

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`
          },
          body: JSON.stringify(syncData)
        }
      )

      const result = await response.json()
      console.log('[JobDetail] sync-to-boxtrax response:', { result: result.result, success: result.success, hasInvoiceToMismatch: !!result.invoiceToMismatch, hasAmbiguousContacts: !!result.ambiguousContacts?.length })

      if (result.result === 'invoice_to_not_bound') {
        setError(result.error || 'Customer has no BoxTop account bound')
        return
      }
      if (result.result === 'address_not_linked') {
        setError(result.error || 'Link the Shipper and Consignee addresses in the Sync Preflight before syncing')
        return
      }
      if (result.result === 'invoice_to_address_mismatch' || result.result === 'ambiguous_contact_match') {
        // Rare race: BoxTop's data changed between the preflight check and this call.
        setError('BoxTop data changed since preflight was approved — please retry sync')
        return
      }

      const isSuccess = result.success && !result.error

      const { data: refreshed, error: refetchError } = await supabase
        .from('jobs')
        .select('boxtrax_synced, boxtrax_ref, boxtrax_job_id, boxtrax_synced_at, boxtrax_error, sync_status')
        .eq('id', jobData.id)
        .single()

      if (refetchError) throw refetchError

      setJobData({ ...jobData, ...refreshed })

      if (result.alreadyExists) {
        // KI26034 P0: idempotency guard blocked this -- nothing was sent to BoxTop.
        showSuccess(`Already exists in BoxTop (JobId=${result.boxtraxJobId}) — not re-sent`)
      } else if (isSuccess) {
        showSuccess('Job synced to Boxtrax successfully')
      } else {
        setError(refreshed?.boxtrax_error || result.error || 'Boxtrax sync completed with warnings')
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to sync with Boxtrax'
      setError(errorMsg)
    } finally {
      setSyncing(false)
    }
  }

  const cleanPayload = (data: any) => {
    const cleaned = { ...data }
    // K9: hbl_* were missing here -- if any is an empty string (e.g. HBL Notify left
    // unset while Importer/Consignor get filled in), Postgres rejects the whole update
    // with a UUID cast error, silently dropping every edit in the same save, not just
    // the empty field.
    const uuidFields = ['importer_id', 'consignor_id', 'notify_id', 'hbl_importer_id', 'hbl_consignor_id', 'hbl_notify_id', 'invoice_to_id', 'overseas_agent_id', 'port_agent_id', 'ovs_house_agent_id', 'break_bulk_agent_id', 'shed_id']
    uuidFields.forEach(field => {
      if (cleaned[field] === '') cleaned[field] = null
    })
    return cleaned
  }

  const handleSave = async () => {
    if (reservationId && reservationCheck !== 'ok') { setError(reservationCheck === 'checking' ? 'Checking the reserved number, try again in a moment' : reservationCheck); return }
    if (!jobData.job_ref) { setError('Job Reference is required'); return }
    if (!JOB_REF_FORMAT.test(jobData.job_ref)) {
      setError('Job Reference must be 1-15 characters, letters/digits/hyphens only')
      return
    }
    if (!jobData.direction) { setError('Direction is required'); return }
    if (!jobData.transport_mode) { setError('Transport Mode is required'); return }
    if (!jobData.job_type) { setError('Job Type is required'); return }
    // A new job needs its Customer from the start (jobs.invoice_to_id, shown in the header). Existing jobs without one are not blocked
    // here -- they are stopped later, when an invoice or a customs declaration is about to be made.
    if (isNew && !jobData.invoice_to_id) { setError('Customer is required -- choose it under the job number'); return }
    if (!jobData.importer_id && !jobData.consignor_id) {
      setError('Please select at least Importer or Consignor')
      return
    }

    const draftsActive = isNew && jobData.job_type === 'CONSOL'
    if (draftsActive && drafts.length > 0) {
      const problems = draftRefProblems(jobData.job_ref, drafts)
      if (Object.keys(problems).length > 0) {
        setActiveTab('consol')
        setError('Fix the house bill reference problems (marked red in the Consol tab) before saving')
        return
      }
    }

    setSaving(true)
    setError(null)

    try {
      // Strip virtual/sub-table keys, Boxtrax sync fields (managed separately), and job_ref
      // (Z30: an existing job's job_ref is renamed via rename_job_ref() below, not this blind
      // update -- it needs to cascade to child job refs atomically) before writing to jobs table
      const { air_details, sea_details, goods, containers, notes, sale_charges, branch_access, boxtrax_synced, boxtrax_ref, boxtrax_job_id, boxtrax_synced_at, boxtrax_error, job_ref: _jobRefField, ...jobsPayload } = jobData

      // Clean empty string FK fields to null and validate required enums
      const cleanedPayload = cleanPayload(jobsPayload)

      // #261: same fix as create-autojobs — a house B/L or HAWB that's actually a
      // container number gets redirected into Containers instead of saved as-is.
      let effectiveSeaDetails = sea_details
      let effectiveAirDetails = air_details
      let effectiveContainers = containers
      let redirectNote = ''
      if (jobData.transport_mode === 'SEA' && isIsoContainerNumber(sea_details?.house_bl_no)) {
        const containerNo = sea_details.house_bl_no.trim().toUpperCase()
        effectiveContainers = [...containers, { container_no: containerNo }]
        effectiveSeaDetails = { ...sea_details, house_bl_no: null }
        redirectNote = ` House B/L "${containerNo}" looked like a container number — moved to Containers.`
      }
      if (jobData.transport_mode === 'AIR' && isIsoContainerNumber(air_details?.hawb_no)) {
        const containerNo = air_details.hawb_no.trim().toUpperCase()
        effectiveContainers = [...effectiveContainers, { container_no: containerNo }]
        effectiveAirDetails = { ...air_details, hawb_no: null }
        redirectNote = ` HAWB "${containerNo}" looked like a container number — moved to Containers.`
      }

      if (!isNew) {
        // ── UPDATE ──────────────────────────────────────────────────────────
        // Z30: rename first (atomically cascades to child job refs), then the rest of the
        // fields via the normal update below.
        if (jobData.job_ref !== originalJobRefRef.current) {
          const { error: renameErr } = await supabase.rpc('rename_job_ref', {
            p_job_id: id!,
            p_new_ref: jobData.job_ref,
          })
          if (renameErr) throw renameErr
          originalJobRefRef.current = jobData.job_ref
        }

        const savedAt = new Date().toISOString()
        const { error: updateErr } = await supabase
          .from('jobs')
          .update({ ...cleanedPayload, updated_at: savedAt })
          .eq('id', id!)
        if (updateErr) throw updateErr
        // The party-address lines re-resolve when updated_at changes (refreshKey); without this they keep showing "save the job first".
        setJobData((prev: any) => ({ ...prev, updated_at: savedAt }))

        // air_details — upsert
        if (jobData.transport_mode === 'AIR') {
          const { id: _adId, job_id: _adJobId, ...airPayload } = effectiveAirDetails
          const { error: airErr } = await supabase
            .from('job_air_details')
            .upsert({ job_id: id!, ...airPayload }, { onConflict: 'job_id' })
          if (airErr) throw airErr
        }

        // sea_details — upsert
        if (jobData.transport_mode === 'SEA') {
          const { id: _sdId, job_id: _sdJobId, ...seaPayload } = effectiveSeaDetails
          const { error: seaErr } = await supabase
            .from('job_sea_details')
            .upsert({ job_id: id!, ...seaPayload }, { onConflict: 'job_id' })
          if (seaErr) throw seaErr
        }

        // goods — delete + re-insert (simplest correct strategy)
        await supabase.from('job_goods').delete().eq('job_id', id!)
        if (goods.length > 0) {
          const goodsRows = goods.map(({ id: _gId, ...g }: any) => ({ ...g, job_id: id! }))
          const { error: goodsErr } = await supabase.from('job_goods').insert(goodsRows)
          if (goodsErr) throw goodsErr
        }

        // charges — ChargesTab persists each Add/Edit/Delete immediately (see its own header
        // comment for why), so there's nothing left to save here.

        // containers — delete + re-insert
        await supabase.from('job_containers').delete().eq('job_id', id!)
        if (effectiveContainers.length > 0) {
          const containerRows = effectiveContainers
            .filter((c: any) => c.container_no || c.seal_no)
            .map(({ id: _ctId, ...c }: any) => ({ ...c, job_id: id! }))
          if (containerRows.length > 0) {
            const { error: containerErr } = await supabase.from('job_containers').insert(containerRows)
            if (containerErr) throw containerErr
          }
        }

        // notes — delete + re-insert
        await supabase.from('job_notes').delete().eq('job_id', id!)
        if (notes.length > 0) {
          const noteRows = notes.map(({ id: _nId, ...n }: any) => ({
            ...n,
            job_id: id!,
            created_by: n.created_by || userEmail,
          }))
          const { error: noteErr } = await supabase.from('job_notes').insert(noteRows)
          if (noteErr) throw noteErr
        }

        // branch_access — delete + re-insert
        await supabase.from('job_branch_access').delete().eq('job_id', id!)
        if (branch_access.length > 0) {
          const accessRows = branch_access.map((b: BranchAccess) => ({
            job_id: id!,
            branch_code: b.branch_code,
            access_type: b.access_type || 'VIEW',
          }))
          await supabase.from('job_branch_access').insert(accessRows)
        }

        showSuccess(`Job saved successfully.${redirectNote}`)
      } else {
        // ── CREATE ──────────────────────────────────────────────────────────
        if (jobData.job_type === 'CONSOL') {
          // Z93b: master + every house bill in ONE transaction (create_consol_with_children).
          // Any failure rolls the whole thing back, so there is never a master with only some of
          // its house bills. Refs are sent explicitly (preview text = what was shown/edited);
          // job_ref_index is assigned server-side under the master row lock.
          const refs = drafts.map((d, i) => effectiveRef(jobData.job_ref, d, i).toUpperCase())
          if (refs.length > 0) {
            const { data: clash, error: clashErr } = await supabase.from('jobs').select('job_ref').in('job_ref', refs.filter(r => REF_FORMAT.test(r)))
            if (clashErr) throw clashErr
            if (clash && clash.length > 0) {
              setActiveTab('consol')
              throw new Error(`Job reference already in use: ${clash.map((c: { job_ref: string }) => c.job_ref).join(', ')}. Nothing was saved.`)
            }
          }
          const { data: created, error: rpcErr } = await supabase.rpc('create_consol_with_children', {
            p_job: { ...cleanedPayload, job_ref: jobData.job_ref, created_by: userEmail },
            p_sea: jobData.transport_mode === 'SEA' ? effectiveSeaDetails : null,
            p_air: jobData.transport_mode === 'AIR' ? effectiveAirDetails : null,
            p_goods: goods.map(({ id: _gId, ...g }: any) => g),
            p_containers: effectiveContainers.filter((c: any) => c.container_no || c.seal_no).map(({ id: _ctId, ...c }: any) => c),
            p_notes: notes.map(({ id: _nId, ...n }: any) => ({ ...n, created_by: userEmail })),
            p_branch_access: branch_access.map((b: BranchAccess) => ({ branch_code: b.branch_code, access_type: b.access_type || 'VIEW' })),
            p_children: drafts.map((d, i) => ({
              job_ref: refs[i],
              house_bl_no: d.houseBlNo || null,
              importer_id: d.importerId || null,
              consignor_id: d.consignorId || null,
              container_no: d.containerNo || null,
              pieces: d.pieces === '' ? null : d.pieces,
              gross_weight: d.grossWeight === '' ? null : d.grossWeight,
              volume: d.volume === '' ? null : d.volume,
              goods_description: d.goodsDescription || null,
            })),
          })
          if (rpcErr) throw rpcErr
          const made = created as { id: string; children: { job_ref: string }[] }
          navigate(`/jobs/${made.id}?tab=consol`, { replace: true })
          showSuccess(
            `Job created successfully.${redirectNote}` +
            (made.children.length > 0
              ? ` ${made.children.length} house bill(s) created (${made.children.map(c => c.job_ref).join(', ')}) — NOT yet synced to BoxTop. Open each job and press Sync.`
              : '')
          )
          return
        }

        const { data: newJob, error: insertErr } = await supabase
          .from('jobs')
          .insert({ ...cleanedPayload, job_ref: jobData.job_ref, created_by: userEmail, created_at: new Date().toISOString() })
          .select()
          .single()
        if (insertErr) throw insertErr

        if (jobData.transport_mode === 'AIR' && Object.keys(effectiveAirDetails).length > 0) {
          const { id: _adId, job_id: _adJobId, ...airPayload } = effectiveAirDetails
          await supabase.from('job_air_details').insert({ job_id: newJob.id, ...airPayload })
        }
        if (jobData.transport_mode === 'SEA' && Object.keys(effectiveSeaDetails).length > 0) {
          const { id: _sdId, job_id: _sdJobId, ...seaPayload } = effectiveSeaDetails
          await supabase.from('job_sea_details').insert({ job_id: newJob.id, ...seaPayload })
        }
        if (goods.length > 0) {
          await supabase.from('job_goods').insert(
            goods.map(({ id: _gId, ...g }: any) => ({ ...g, job_id: newJob.id }))
          )
        }
        const newContainerRows = effectiveContainers
          .filter((c: any) => c.container_no || c.seal_no)
          .map(({ id: _ctId, ...c }: any) => ({ ...c, job_id: newJob.id }))
        if (newContainerRows.length > 0) {
          await supabase.from('job_containers').insert(newContainerRows)
        }
        if (notes.length > 0) {
          await supabase.from('job_notes').insert(
            notes.map(({ id: _nId, ...n }: any) => ({
              ...n,
              job_id: newJob.id,
              created_by: userEmail,
            }))
          )
        }
        if (branch_access.length > 0) {
          await supabase.from('job_branch_access').insert(
            branch_access.map((b: BranchAccess) => ({
              job_id: newJob.id,
              branch_code: b.branch_code,
              access_type: b.access_type || 'VIEW',
            }))
          )
        }

        navigate(`/jobs/${newJob.id}`, { replace: true })
        showSuccess(`Job created successfully.${redirectNote}`)
      }
    } catch (err) {
      let errorMsg = 'Failed to save job'
      if (err instanceof Error) {
        errorMsg = err.message
        if (err.message.includes('violates check constraint')) {
          const match = err.message.match(/constraint "([^"]+)"/)
          const constraint = match ? match[1] : 'unknown'
          if (constraint.includes('status')) {
            errorMsg = `Invalid status value. Allowed values: PRE, CUS, CTR, DEL, PDY, POD, INV, WAR, RET, COM, PRO, CAN, DEF, WKD, EXP, SDI, REQ`
          } else if (constraint.includes('job_ref')) {
            errorMsg = 'Job Reference must be 1-15 characters, letters/digits/hyphens only'
          } else {
            errorMsg = `Constraint violation: ${constraint}`
          }
        } else if (err.message.includes('duplicate key') && err.message.includes('job_ref')) {
          errorMsg = `Job reference "${jobData.job_ref}" is already in use`
        }
      }
      setError(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    )
  }

  if (!isNew && jobData.job_type === 'GROUP') return <GroupPage jobData={jobData} />

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">

        {/* Reminder banner */}
        {!reminderDismissed && reminders.length > 0 && (
          <div className="mb-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-600 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 min-w-0">
                <span className="text-amber-500 text-lg flex-shrink-0 mt-0.5">⚠</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
                    Special Instructions {reminders.length > 1 ? `(${reminders.length} companies)` : `— ${reminders[0].name}`}
                  </p>
                  {reminders.map((r, i) => (
                    <div key={i} className="text-sm text-amber-700 dark:text-amber-400">
                      {reminders.length > 1 && <span className="font-medium">{r.name}: </span>}
                      {r.reminder}
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => setReminderDismissed(true)}
                className="text-amber-500 hover:text-amber-700 flex-shrink-0 text-lg leading-none mt-0.5">✕</button>
            </div>
          </div>
        )}

        {copiedFromRef && (
          <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2.5 text-sm text-blue-700 dark:text-blue-300">
            Copied from <span className="font-mono font-semibold">{copiedFromRef}</span> · please complete MBL / Container / ETA
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-4 mb-1">
                <button
                  onClick={() => navigate('/jobs')}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1"
                >
                  ← Back to Jobs
                </button>
                {/* Only on a house bill of a Consol master: straight to that master's Consol tab (the list of all its house bills). */}
                {consolParent && (
                  <button
                    onClick={() => navigate(`/jobs/${consolParent.id}?tab=consol`)}
                    title={`Back to the Consol ${consolParent.ref} (house bills)`}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                  >
                    ← {consolParent.ref}
                  </button>
                )}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {isNew ? 'Create New Job' : jobData.job_ref}
              </h1>
              {!isNew && (
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  {jobData.direction === 'IMP' ? 'Import' : 'Export'} · {jobData.transport_mode} · {jobData.job_type}
                </p>
              )}
              <JobCustomerLine jobData={jobData} setJobData={setJobData} readOnly={jobData.is_locked === true} userEmail={userEmail} />
            </div>
            {/* Z73/Z73.2: moved here from the bottom footer -- staff weren't finding the small
                footer button. This is the "Click eCustoms on a Job" / "Generate eCustoms
                Declaration" entry point from the spec, made hard to miss instead. */}
            {!isNew && (
              <div className="flex flex-col items-end gap-1.5">
                {importerPoaStatus && (
                  <PoaStatusBadge badge={importerPoaStatus.badge} title={formatPoaStatusLine(importerPoaStatus)} />
                )}
                {jobData.job_type !== 'CONSOL' && (
                  <button
                    onClick={() => setShowDuplicate(true)}
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Duplicate this Job
                  </button>
                )}
                {!groupParent && <button
                  onClick={() => navigate(`/jobs/${id}/ecustoms`)}
                  className="px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold shadow-sm flex items-center gap-2"
                >
                  📋 Generate eCustoms Declaration
                </button>}
              </div>
            )}
          </div>

          {/* Key info bar */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                  Job Reference
                </label>
                <input
                  type="text"
                  value={jobData.job_ref}
                  readOnly={!!reservationId}
                  onChange={e => {
                    const next = e.target.value.toUpperCase()
                    setJobData({ ...jobData, job_ref: next })
                    setJobRefFormatError(next && !JOB_REF_FORMAT.test(next) ? '1-15 chars: A-Z, 0-9, hyphen only' : null)
                  }}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white font-mono font-bold ${reservationId ? 'bg-gray-100 dark:bg-gray-600 ' : ''}${jobRefFormatError ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  placeholder="KI/KE + 5 digits"
                />
                {jobRefFormatError && (
                  <p className="text-xs text-red-500 mt-1">{jobRefFormatError}</p>
                )}
                {reservationId && reservationCheck === 'ok' && (
                  <p className="text-xs text-teal-700 dark:text-teal-400 mt-1">Using reserved number {jobData.job_ref}</p>
                )}
                {reservationId && reservationCheck !== 'ok' && reservationCheck !== 'checking' && (
                  <p className="text-xs text-red-600 mt-1">{reservationCheck}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                  Direction <span className="text-red-500">*</span>
                </label>
                <select
                  value={jobData.direction || ''}
                  onChange={e => setJobData({ ...jobData, direction: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">— Select —</option>
                  <option value="IMP">▲ Import</option>
                  <option value="EXP">▼ Export</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                  Mode <span className="text-red-500">*</span>
                </label>
                <select
                  value={jobData.transport_mode || ''}
                  onChange={e => setJobData({ ...jobData, transport_mode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">— Select —</option>
                  <option value="AIR">Air</option>
                  <option value="SEA">Sea</option>
                  <option value="ROAD">Road</option>
                  <option value="DOMESTIC">Rail</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                  Job Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={jobData.job_type || ''}
                  onChange={e => setJobData({ ...jobData, job_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">— Select —</option>
                  <option value="SINGLE">Single</option>
                  <option value="CONSOL">Consol</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                  Status
                </label>
                <SearchableSelect
                  options={[
                    { id: 'PRE', label: 'Pre-Alert' },
                    { id: 'CUS', label: 'Custom' },
                    { id: 'CTR', label: 'Container' },
                    { id: 'DEL', label: 'Delivered' },
                    { id: 'PDY', label: 'Pending Delivery' },
                    { id: 'POD', label: 'Proof of Delivery' },
                    { id: 'INV', label: 'Invoiced' },
                    { id: 'WAR', label: 'Warehouse' },
                    { id: 'RET', label: 'Return' },
                    { id: 'COM', label: 'Completed' },
                    { id: 'PRO', label: 'Processing' },
                    { id: 'CAN', label: 'Cancelled' },
                    { id: 'DEF', label: 'Default' },
                    { id: 'WKD', label: 'Worked' },
                    { id: 'EXP', label: 'Expired' },
                    { id: 'SDI', label: 'Standby Delivery Item' },
                    { id: 'REQ', label: 'Requested' }
                  ]}
                  value={jobData.status}
                  onChange={(value) => setJobData({ ...jobData, status: value })}
                  placeholder="Select status..."
                />
              </div>
              {!isNew && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                    Owner
                  </label>
                  <p className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    {ownerName || <span className="text-gray-400 dark:text-gray-500 italic">Unmapped — set in Admin &gt; Manage Users</span>}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Branch Access card */}
        {allBranches.length > 0 && (
          <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="flex flex-wrap items-center gap-6">
              {/* Primary branch */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Primary Branch
                </span>
                {canEditBranch ? (
                  <div className="flex-1 max-w-xs">
                    <SearchableSelect
                      options={[
                        { id: '', label: '— None —' },
                        ...allBranches.map(b => ({
                          id: b.code,
                          label: `${b.code} — ${b.name}`
                        }))
                      ]}
                      value={jobData.branch_code || ''}
                      onChange={(value) => setJobData({ ...jobData, branch_code: value || null })}
                      placeholder="Select branch..."
                    />
                  </div>
                ) : (
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 font-mono">
                    {jobData.branch_code || '—'}
                  </span>
                )}
              </div>

              {/* Shared with */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Shared With
                </span>
                {allBranches
                  .filter(b => b.code !== jobData.branch_code)
                  .map(b => {
                    const isShared = (jobData.branch_access as BranchAccess[]).some(a => a.branch_code === b.code)
                    if (!canEditBranch) {
                      return isShared ? (
                        <span key={b.code} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-mono">
                          {b.code}
                        </span>
                      ) : null
                    }
                    return (
                      <label key={b.code} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isShared}
                          onChange={() => {
                            const current = jobData.branch_access as BranchAccess[]
                            const next = isShared
                              ? current.filter(a => a.branch_code !== b.code)
                              : [...current, { branch_code: b.code, access_type: 'VIEW' }]
                            setJobData({ ...jobData, branch_access: next })
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{b.code}</span>
                      </label>
                    )
                  })}
                {!canEditBranch && !(jobData.branch_access as BranchAccess[]).length && (
                  <span className="text-sm text-gray-400">—</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Boxtrax Sync Status - Show for AUTOJOBS imported jobs */}
        {BOXTOP_SYNC_ENABLED && !isNew && jobData.import_source === 'AUTOJOBS' && (
          <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            {/* BoxTop support (Aug 2026): no update endpoint exists -- once a job is synced,
                further edits here never reach BoxTop. Warn before that first sync, not after. */}
            {jobData.sync_status !== 'synced' && (
              <p className="mb-3 text-xs text-amber-700 dark:text-amber-400">
                ⚠️ Changes to job data after BoxTop sync will NOT propagate to BoxTop. Ensure data is complete before syncing.
              </p>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Boxtrax Sync
                </span>
                {/* #16: branches on the real jobs.sync_status ('pending'/'synced'/'failed',
                    written solely by sync-to-boxtrax) instead of the boxtrax_synced/
                    boxtrax_error pair, which never reflected an unsynced-vs-in-flight
                    distinction. `syncing` (local state, set for the duration of this
                    button's own request) takes priority so the in-flight state is visible
                    immediately without waiting on a round-trip. */}
                {syncing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">🔄 Syncing…</span>
                    </div>
                  </div>
                ) : jobData.sync_status === 'synced' ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full" />
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">✅ Synced</span>
                      {jobData.boxtrax_ref && <span className="text-gray-500 dark:text-gray-400"> — Ref: {jobData.boxtrax_ref}</span>}
                      {jobData.boxtrax_synced_at && (
                        <span className="text-gray-500 dark:text-gray-400"> on {new Date(jobData.boxtrax_synced_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ) : jobData.sync_status === 'failed' ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full" />
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold text-red-600 dark:text-red-400">❌ Sync failed</span>
                      {jobData.boxtrax_error && <span className="text-gray-500 dark:text-gray-400"> — {jobData.boxtrax_error}</span>}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full" />
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">⚪ Not synced yet</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Task V: always visible regardless of sync_status -- staff need to see past
                    attempts (e.g. to hand BoxTop support the full request/response JSON for a
                    failed or wrong-prefix sync) whether or not the job is currently synced. */}
                <button
                  onClick={() => setSyncHistoryOpen(true)}
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Sync History
                </button>
                {/* KI26034 P0: jobs.boxtrax_job_id set (BoxTop already has a record, even
                    if boxtrax_ref never resolved) means a plain "Sync Now" click would just
                    call CreateJobData again and create another duplicate -- there's no
                    update endpoint yet. Replace the button with a static label + an opt-in,
                    confirmed "Force re-sync" for when staff really want another attempt. */}
                {jobData.boxtrax_job_id && (jobData.sync_status === 'partial' || jobData.sync_status === 'synced') ? (
                  <span className="inline-flex items-center gap-3 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      BoxTop record exists (JobId={jobData.boxtrax_job_id})
                    </span>
                    <button
                      onClick={() => handleBoxtraxSync(true)}
                      disabled={syncing}
                      title="BoxTop has no update endpoint -- this creates a NEW, separate record rather than updating the existing one."
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Force re-sync
                    </button>
                  </span>
                ) : jobData.sync_status !== 'synced' && (
                  <button
                    onClick={() => handleBoxtraxSync(false)}
                    disabled={syncing}
                    title="Once synced, edits here will NOT propagate to BoxTop -- make sure job data is complete first."
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      syncing
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {syncing ? 'Syncing...' : jobData.sync_status === 'failed' ? 'Retry' : 'Sync Now'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {BOXTOP_SYNC_ENABLED && !isNew && jobData.import_source === 'AUTOJOBS' && (
          <SyncHistoryDialog
            isOpen={syncHistoryOpen}
            onClose={() => setSyncHistoryOpen(false)}
            jobId={jobData.id}
            jobRef={jobData.job_ref}
          />
        )}

        {/* Z58.7: consolidated Sync Preflight -- InvoiceTo/Shipper/Consignee/Notify all shown,
            every party has a visible status, "Approve All & Sync" only enabled once every
            needs_decision party is resolved. */}
        {BOXTOP_SYNC_ENABLED && pendingPreflight && (
          <SyncPreflightModal
            jobRef={jobData.job_ref}
            onCancel={() => setPendingPreflight(null)}
            onApproved={() => {
              const { force } = pendingPreflight
              setPendingPreflight(null)
              runBoxtraxSync(force)
            }}
          />
        )}

        {groupParent && (
          <div className="mt-4 rounded-lg border border-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-700 px-4 py-3 text-sm text-indigo-900 dark:text-indigo-200">
            This job is a shipment of group <Link to={`/jobs/${groupParent.id}?row=${id}`} className="font-mono font-bold underline">{groupParent.ref}</Link>.
            It is not billed (charges and the invoice are on the group) and has no customs.
          </div>
        )}

        {/* Notifications */}
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-4 font-bold">×</button>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg">
            {successMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <div className="flex">
              {TABS.map(tab => {
                // Documents, Consol and Transport tabs only make sense for saved jobs
                // Z93b: on a new job the Consol tab exists once Job Type = Consol (draft house bills)
                if (groupParent && (tab.name === 'charges' || tab.name === 'customs')) return null
                if (tab.name === 'consol' && isNew) { if (jobData.job_type !== 'CONSOL') return null }
                else if ((tab.name === 'documents' || tab.name === 'transport' || tab.name === 'charges') && isNew) return null
                const tabLabel = tab.name === 'consol' && jobData.job_type === 'CONSOL'
                  ? `Consol (House bills)${isNew ? ` · ${drafts.length}` : ''}`
                  : tab.label
                return (
                  <button
                    key={tab.name}
                    onClick={() => setActiveTab(tab.name)}
                    className={`px-5 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                      activeTab === tab.name
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {tabLabel}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'addresses'  && <AddressesTab  jobData={jobData} setJobData={setJobData} />}
            {activeTab === 'ship'       && <ShipDetailsTab jobData={jobData} setJobData={setJobData} />}
            {activeTab === 'goods'      && <GoodsTab       jobData={jobData} setJobData={setJobData} />}
            {activeTab === 'charges'    && !isNew && <ChargesTab jobId={id!} defaultBillToId={jobData.invoice_to_id} />}
            {activeTab === 'customs'    && <CustomsFinancialTab jobData={jobData} setJobData={setJobData} />}
            {activeTab === 'transport'  && !isNew && <TransportTab jobId={id!} jobData={jobData} />}
            {activeTab === 'milestones' && !isNew && <MilestonesTab jobId={id!} jobRef={jobData.job_ref} />}
            {activeTab === 'notes'      && <NotesTab       jobData={jobData} setJobData={setJobData} />}
            {activeTab === 'documents'  && !isNew && <DocumentsTab jobId={id!} />}
            {activeTab === 'consol'     && !isNew && <ConsolTab    jobData={jobData} />}
            {activeTab === 'consol'     && isNew && jobData.job_type === 'CONSOL' && (
              <ConsolDraftsTab masterRef={jobData.job_ref} transportMode={jobData.transport_mode} drafts={drafts} setDrafts={setDrafts} />
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={() => navigate('/jobs')}
            className="px-6 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {saving ? 'Saving…' : isNew ? 'Create Job' : 'Save Changes'}
          </button>
        </div>
      </div>
      {showDuplicate && id && (
        <DuplicateJobModal sourceJobId={id} onClose={() => setShowDuplicate(false)} />
      )}
    </div>
  )
}
